import {
  ProjectionProviderType,
  ProviderActionStatusEnum,
  ProviderBadgeStyle,
} from "@app/projects/enumerators";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { JSX, useContext, useReducer, useRef, useState } from "react";
import { Popup } from "react-map-gl/mapbox";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import DeleteProjectionProvider, {
  WrappedDeleteProjectionProvider
} from "@app/projects/projection-provider/DeleteProjectionProvider";
import { FilterContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext";
import {
  NetworkCoverageMapProviderPopupProps
} from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { convertServicingProvidersToAccessPoints } from "@app/projects/projection-provider/utils";
import { providerActionStatusEnumToProjectionProviderType } from "@app/projects/utility";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { NetworkCoverageMapDataContext } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext';
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ImpactProviderMapResults, ProviderMapResults } from "@app/projects/results/common/mapping";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { buildServicingProviderFromProviderMapResults } from "@app/projects/servicing-provider/utility";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

const NetworkCoverageMapProviderPopup: React.FC<NetworkCoverageMapProviderPopupProps> = (
  { provider, onClose, processProviderChanges, setProjectHasUnprocessedProjectionProviders }
) => {
  const filterContext = useContext(FilterContext);
  const projectContext = useContext(CountyProjectContext);
  const userContext = useContext(UserContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext);

  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);

  const [isDeleteProjectionProviderModalOpen, setIsDeleteProjectionProviderModalOpen] = useState<boolean>(false);

  const deleteProjectionProviderRef = useRef<WrappedDeleteProjectionProvider>(null);

  const isNetworkProvider = provider.providerNetworkType == ProviderNetworkTypeEnum.Network;
  const isTerminatedProvider = provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated;
  const isTargetProvider = provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted;
  const isExcludedProvider = provider.scenarioModelProviderStatus  === ProviderActionStatusEnum.FullyExcluded;
  const isProjected = isTargetProvider || isExcludedProvider;

  const isTargetDisabled = projectContext.project.isReadOnly || projectContext.project.isEditRestricted;
  const isScenarioModelActionsVisible = projectContext.runConfiguration?.isLatestPublished || !projectContext.runConfiguration?.datePublished;

  const pciEnabled = filterContext.adequacyTimeslices.find(prc => prc.projectRunConfigurationId == filterContext.selectedTimeslice).showPciData;

  const getPriorProjectionProvider = (providerActionStatus: ProviderActionStatusEnum) => {
    if (providerActionStatus === ProviderActionStatusEnum.Unknown) {
      return null;
    } else {
      return providerActionStatus;
    }
  };

  const getProviderFlyout = (): React.ReactNode => {
    if (!!provider) {
      return (
        <ServicingProviderProfile
          isProjected={isProjected}
          isTerminated={isTerminatedProvider}
          nextClosestProvider={false}
          project={projectContext.project}
          provider={buildServicingProviderFromProviderMapResults(provider)}
          regionSetId={parseInt(filterContext.selectedServiceArea)}
          runConfiguration={projectContext.runConfiguration}
          providerNetworkType={provider.providerNetworkType}
          specialtyGroupCode={filterContext.selectedSpecialtyGroupCode.key}
        />
      );
    }
  };

  const getPreviewTargetingContent = (): React.ReactNode => {
    const showAvgDist = projectContext.runConfiguration.showDistance && projectContext.project.showAverageDistance;
    const showAvgTime = projectContext.runConfiguration.showTime && projectContext.project.showAverageTime;
    const showWithTimeAndDistanceColumns = projectContext.runConfiguration.showWithTimeAndDistanceColumns;
    const showNetNew = userContext.roles.includes(Roles.VOLUME_TARGET);
    const selectedProvider = provider as ImpactProviderMapResults;

    return (
      <SimulateProviderTargeting
        processingContent={
          (() => getCreateProjectionProvider(
            ProviderActionStatusEnum.FullyTargeted,
            getPriorProjectionProvider(selectedProvider.scenarioModelProviderStatus)
          ))()
        }
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={provider.providerNetworkType}
        regionSetId={parseInt(filterContext.selectedServiceArea)}
        // In mapping v2, so far we support targetting individual providers only
        selectedProviders={[buildServicingProviderFromProviderMapResults(selectedProvider)]}
        specialtyGroupCode={filterContext.selectedSpecialtyGroupCode.key}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns, }}
        user={userContext}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        isZipProject={false}
      />
    );
  };

  const getCreateProjectionProvider = (
    providerActionStatus: ProviderActionStatusEnum,
    priorProviderActionStatus: ProviderActionStatusEnum
  ) => {
    let priorProjectionProviderProjectIds: number[];
    const selectedProvider = provider as ImpactProviderMapResults;
    const selectedProviders: ServicingProvider[] = [buildServicingProviderFromProviderMapResults(selectedProvider)];

    if (priorProviderActionStatus) {
      priorProjectionProviderProjectIds = selectedProviders.map((r) => {
        const projectionProvider = r as ProjectionProvider;
        return projectionProvider.projectionProviderProjectId;
      });
    }

    return (
      <ProjectionProviderDetails
        accessPoints={convertServicingProvidersToAccessPoints(selectedProviders, provider.providerNetworkType, filterContext.selectedSpecialtyGroupCode.key)}
        showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        hasProvidersWithoutNPI={selectedProviders.some((provider) => provider.npi === undefined)}
        networkAssessmentAnalysisType={projectContext.runConfiguration.networkAssessmentAnalysisType}
        priorProjectionProviderProjectIds={priorProjectionProviderProjectIds}
        priorProjectionProviderType={providerActionStatusEnumToProjectionProviderType(priorProviderActionStatus)}
        projectId={projectContext.project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={providerActionStatusEnumToProjectionProviderType(providerActionStatus)}
        providerNetworkType={selectedProvider.providerNetworkType}
        onProjectionProvidersCreated={handleProjectionProviderCreatedOrUpdated}
        specialtyGroupCode={filterContext.selectedSpecialtyGroupCode.key}
        isZipProject={false}
        isRunning={projectContext.project?.isRunning}
      />
    );
  }

  const getDeleteProjectionProvider = () => {
    const selectedProvider = provider as ImpactProviderMapResults;

    return (
      <DeleteProjectionProvider
        netNewServicingProviderProjectIds={[]}
        projectId={projectContext.project.projectId}
        projectionProviderProjectIds={[selectedProvider.scenarioModelProviderProjectId]}
        projectionProviderType={providerActionStatusEnumToProjectionProviderType(selectedProvider.scenarioModelProviderStatus)}
        providerNetworkType={selectedProvider.providerNetworkType}
        onProjectionProvidersDeleted={handleProjectionProviderDeleted}
        wrapperComponentType="modal"
        ref={deleteProjectionProviderRef}
      />
    );
  }

  const handleProjectionProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      processProviderChanges(false);
    } else {
      setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
    }
  }

  const handleProjectionProviderDeleted = async (runProjection: boolean) => {
    if (runProjection) {
      processProviderChanges(false);
    } else {
      setProjectHasUnprocessedProjectionProviders(true);
    }
    setIsDeleteProjectionProviderModalOpen(false);
  }

  const handleConfirmDeleteProjectionProvider = async () => {
    //Execute the handleSubmit method in the DeleteProjectionProvider component
    await deleteProjectionProviderRef.current?.handleSubmit();
  };

  const getVolumeLabelDisplay = (provider: ProviderMapResults) => {
    if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.Total) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.totalClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.totalClaimsVolume ?? "---";
      }
    }
    else if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.Commercial) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.commercialClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.commercialClaimsVolume ?? "---";
      }
    }
    else if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.Medicare) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.medicareClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.medicareClaimsVolume ?? "---";
      }
    }
    else if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.Medicaid) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.medicaidClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.medicaidClaimsVolume ?? "---";
      }
    }
    else if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.VA) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.vaClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.vaClaimsVolume ?? "---";
      }
    }
    else if (mapDataContext.selectedLineOfBusiness === ClaimsActivityLineOfBusinessTypeEnum.TRICARE) {
      if (mapDataContext.pcaIndicatorIncludesInsufficient(provider.tricareClaimsVolume)) {
        return ClaimsActivityVolumeLabelEnum.InsufficientData;
      }
      else {
        return provider.tricareClaimsVolume ?? "---";
      }
    }
  }

  return (
    <Popup
      anchor="bottom"
      closeButton={true}
      closeOnClick={false}
      latitude={provider.latitude}
      longitude={provider.longitude}
      onClose={onClose}
      offset={[0, -20]}
      className="network-coverage-map__provider-popup"
    >
      <>
        <div className="provider-popup__title">{provider.displayName}</div>
        <div className="provider-popup__separator" />
        <div className="provider-popup__badge">
          <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} providerNetworkType={provider.providerNetworkType} />
          {isScenarioModelActionsVisible &&
            <ProviderActionStatusBadge
              badgeStyle={ProviderBadgeStyle.Outline}
              status={provider.scenarioModelProviderStatus}
              hideWhenNullActionStatus={true}
            />
          }
        </div>

        <div className="provider-popup__row">
          <strong>NPI:</strong> {provider.npi}
        </div>

        <div className="provider-popup__row">
          <strong>Address:</strong> {provider.address}, {provider.city}, {provider.state} {provider.zip}
        </div>

        <div className="provider-popup__row">
          {isNetworkProvider ?
            <>
              <strong>Serves:</strong> {provider.servesPercent}%
            </>
            :
            <>
              <strong>Serves:</strong> {(provider as ImpactProviderMapResults).servesPercent}%
              <strong className="impact-label">Gains:</strong> {(provider as ImpactProviderMapResults).gainsPercent}%
              <strong className="impact-label" >Impact:</strong> {(provider as ImpactProviderMapResults).impactsPercent}%
            </>
          }
        </div>

        {(pciEnabled) ?
          (
            <>
              {mapDataContext.selectedLineOfBusiness &&
                (
                  <div className="provider-popup__row">
                    <strong>{mapDataContext.selectedLineOfBusiness} Claims Volume:</strong> {getVolumeLabelDisplay(provider as ProviderMapResults)}
                  </div>
                )
              }
              <div className="provider-popup__row">
                <strong>Quality Score:</strong> {provider.effectivenessRank ?? "---"}
              </div>

              <div className="provider-popup__row">
                <strong>Cost Score:</strong> {provider.efficiencyRank ?? "---"}
              </div>
            </>
          ) : null
        }

        {/* Provider Details flyout */}
        <div className="provider-popup__row">
          <FlyoutLink
            classNames="provider-details-button"
            content={{
              body: getProviderFlyout() as JSX.Element,
              title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
            }}
            size="40"
            testId='network-coverage-map__provider-popup-view-provider-details-button-flyout'>
            View Provider Details
          </FlyoutLink>
        </div>

        {!isNetworkProvider && !isTargetProvider && !isExcludedProvider && isScenarioModelActionsVisible  && (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
            <div className="provider-popup__row">
              <FlyoutLink
                classNames="qes-button-primary"
                content={{
                  body: getPreviewTargetingContent() as JSX.Element,
                  title: `Target Provider`
                }}
                disabled={isTargetDisabled}
                testId='network-coverage-map__provider-popup-target-provider-button-flyout-with-preview'>
                Target Provider
              </FlyoutLink>
            </div>
          </RoleRestricted>
        )}

        {isTargetProvider && isScenarioModelActionsVisible  && (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
            <div className="provider-popup__row">
              <QESButton
                qesType="primary"
                disabled={isTargetDisabled}
                onClick={() => setIsDeleteProjectionProviderModalOpen(true)}
                qesTestId="network-coverage-map__provider-popup-remove-provider-from-targeting-modal">
                Remove Target
              </QESButton>
              <Modal
                closeModal={() => setIsDeleteProjectionProviderModalOpen(false)}
                isVisible={isDeleteProjectionProviderModalOpen}
                content={{
                  body: getDeleteProjectionProvider(),
                  cancelButtonText: "Cancel",
                  confirmButtonAction: handleConfirmDeleteProjectionProvider,
                  confirmButtonText: "Submit",
                  confirmButtonType: "primary",
                  title: "Delete Targeted Provider",
                }}

              />
            </div>
          </RoleRestricted>
        )}
      </>
    </Popup>
  );
};

export default NetworkCoverageMapProviderPopup;